import dayjs from "dayjs"
import type { ReactNode } from "react"
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react"
import type { VariableSizeList } from "react-window"

import { getExpectedGraphHeight } from "./getExpectedGraphHeight"
import { getExpectedGraphWidth } from "./getExpectedGraphWidth"
import { GraphContext } from "./GraphContext"
import { pointsPerHourToHoursPerDivider } from "./pointsPerHourToHoursPerDivider"
import { useGraphBounds } from "./useGraphBounds"
import { useGraphZoom } from "./useGraphZoom"
import { usePreserveScrollPosition } from "./usePreserveScrollPosition"

import { useFollowedUsers } from "src/contexts"
import { useRefValue } from "src/hooks"
import type {
  ReactWindowAutoSizerArgs,
  ReactWindowOnScrollArgs,
} from "src/types"

export const yAxisWidth = 40
export const yAxisLabelFontSize = 12
export const xAxisHeight = 18
export const timeDividerWidth = 80

const expectedWidth = getExpectedGraphWidth(yAxisWidth)
const expectedHeight = getExpectedGraphHeight()

interface Props {
  children?: ReactNode;
  scrollToDate?: Date;
  setFeedScrollToDate: (date: Date) => void; // this is called to scroll the feed
}

export function GraphContextProvider(props: Props) {
  const {
    scrollToDate,
    setFeedScrollToDate,
  } = props

  const listRef = useRef<VariableSizeList>(null)

  const { currentDeviceSettings } = useFollowedUsers()
  const {
    chart_top: topMgdl,
    chart_bottom: bottomMgdl,
    graph_icon_scale: graphIconScale,
  } = currentDeviceSettings

  const graphIconSize = graphIconScale * 16

  const [
    zoom,
    setZoom,
  ] = useGraphZoom()

  const [
    calculatedWidth,
    setCalculatedWidth,
  ] = useState<number>(expectedWidth)

  const [
    calculatedHeight,
    setCalculatedHeight,
  ] = useState<number>(expectedHeight)

  const hoursPerGraphItem = zoom

  const scrollPositionRef = useRef<number>(0)

  const height = calculatedHeight

  const pointsPerHour = useMemo(
    () => calculatedWidth / zoom,
    [
      calculatedWidth,
      zoom,
    ],
  )

  const [
    graphStart,
    graphEnd,
  ] = useGraphBounds(pointsPerHour)

  const [
    graphScrollDate,
    setGraphScrollDate,
  ] = useState<Date>(
    dayjs().toDate(),
  )

  const numberOfItems = useMemo(
    () => {
      const items = dayjs(graphEnd)
        .diff(
          graphStart,
          "hours",
          true,
        ) / hoursPerGraphItem

      // + 1 item which adds a buffer space so that oldest time marker is not clipped
      return Math.ceil(items) + 1
    },
    [
      graphStart,
      graphEnd,
      hoursPerGraphItem,
    ],
  )

  // rerender the list when the number of items or graph boundaries change
  // otherwise it will use cached values for item size and the time will be off
  useLayoutEffect(
    () => {
      listRef.current?.resetAfterIndex(
        0,
        true,
      )
    },
    [
      listRef,
      numberOfItems,
      graphStart,
      graphEnd,
    ],
  )

  usePreserveScrollPosition(
    calculatedWidth,
    graphEnd,
    pointsPerHour,
    listRef,
    scrollPositionRef,
  )

  // this function updates the graph date to the time at the center of the graph
  const computeTime = useCallback(
    () => {
      const scrollPosition = scrollPositionRef.current

      const hours = scrollPosition / pointsPerHour + zoom / 2
      const time = dayjs(graphEnd)
        .subtract(
          hours,
          "hours",
        )
        .toISOString()

      setGraphScrollDate(new Date(time))
      setFeedScrollToDate(
        dayjs(time)
          .add(
            zoom / 2,
            "hours",
          )
          .toDate(),
      )
    },
    [
      setFeedScrollToDate,
      scrollPositionRef,
      graphEnd,
      pointsPerHour,
      zoom,
    ],
  )

  // update the graph date when the user scrolls
  const onScroll = useCallback(
    (scrollEvent: ReactWindowOnScrollArgs) => {
      scrollPositionRef.current = scrollEvent.scrollOffset
      computeTime()
    },
    [
      scrollPositionRef,
      computeTime,
    ],
  )

  // re-execute computeTime when the zoom or graph end changes
  useEffect(
    () => {
      computeTime()
    },
    [computeTime],
  )

  // this function tracks the measurements of the flat list
  const onResize = useCallback(
    (resizeEvent: ReactWindowAutoSizerArgs) => {
      setCalculatedWidth(resizeEvent.width)
      setCalculatedHeight(resizeEvent.height)
    },
    [
      setCalculatedWidth,
      setCalculatedHeight,
    ],
  )

  // this function will scroll to the given date when invoked
  const onScrollToDateChange = useCallback(
    (date: Date) => {
      const list = listRef.current

      if (!list) {
        return
      }

      const diffInHours = dayjs(graphEnd)
        .diff(
          dayjs(date),
          "hours",
          true,
        ) - hoursPerGraphItem / 2 // this centers the time we are scrolling to

      // + 1 to tiebreak the graph heading
      const offset = pointsPerHour * diffInHours + 1
      list.scrollTo(offset)
    },
    [
      listRef,
      graphEnd,
      pointsPerHour,
      hoursPerGraphItem,
    ],
  )

  // a ref that points to the onScrollToDateChange function
  const onScrollToDateChangeRef = useRefValue(onScrollToDateChange)

  /*
   * scroll to "scrollToDate" whenever it changes
   * it is changed by the date picker above the graph and by the feed
   */
  useEffect(
    () => {
      if (scrollToDate) {
        onScrollToDateChangeRef.current(scrollToDate)
      }
    },
    [
      scrollToDate,
      onScrollToDateChangeRef,
    ],
  )

  const value = useMemo(
    () => ({
      graphStart,
      graphEnd,
      pointsPerHour,
      ...pointsPerHourToHoursPerDivider(pointsPerHour),
      topMgdl,
      bottomMgdl,
      numberOfItems,
      hoursPerGraphItem,
      onScroll,
      onResize,
      listRef,
      yAxisHeight: height - xAxisHeight,
      graphActivityHeight: graphIconSize + 8,
      graphIconSize,
      zoom,
      setZoom,
      graphScrollDate,
      scrollPositionRef,
    }),
    [
      graphStart,
      graphEnd,
      pointsPerHour,
      topMgdl,
      bottomMgdl,
      numberOfItems,
      hoursPerGraphItem,
      onScroll,
      onResize,
      listRef,
      graphIconSize,
      zoom,
      setZoom,
      graphScrollDate,
      height,
    ],
  )

  return (
    <GraphContext.Provider value={value}>
      {props.children}
    </GraphContext.Provider>
  )
}
